import React, { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  FileKey,
  FileText,
  Globe,
  HelpCircle,
  Info,
  Key,
  KeyRound,
  Link2,
  Lock,
  MonitorUp,
  Package,
  PlayCircle,
  Plug,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { login_success } from "../../actions/user";
import methodModel from "../../methods/methods";
import environment from "../../environment";
import { setAuthorizationToken } from "../../methods/auth";

const DESKTOP_SYNC_JOBS = [
  {
    key: "customers",
    label: "Customers",
    endpoint: "qb-desktop/sync/customers",
    icon: Users,
    description: "Sync customer profiles, contact info, and addresses.",
    color: "from-blue-500 to-indigo-600",
  },
  {
    key: "items",
    label: "Products",
    endpoint: "qb-desktop/sync/items",
    icon: Package,
    description: "Sync inventory, non-inventory items, and services.",
    color: "from-emerald-500 to-teal-600",
  },
  {
    key: "invoices",
    label: "Invoices",
    endpoint: "qb-desktop/sync/invoices",
    icon: ReceiptText,
    description: "Sync invoice records, line items, and balances.",
    color: "from-purple-500 to-violet-600",
  },
  {
    key: "estimates",
    label: "Estimates",
    endpoint: "qb-desktop/sync/estimates",
    icon: ClipboardList,
    description: "Sync estimate quotes, amounts, and statuses.",
    color: "from-amber-500 to-orange-600",
  },
];

const DESKTOP_REQUIREMENTS = [
  {
    title: "QuickBooks Desktop Open",
    description: "QuickBooks Desktop must be running with your company file open.",
  },
  {
    title: "Web Connector Installed",
    description: "QuickBooks Web Connector (QBWC) installed on the same computer.",
  },
  {
    title: "Admin Permissions",
    description: "Logged into QuickBooks Desktop as Admin in single-user mode for setup.",
  },
];

const ENHANCED_DESKTOP_STEPS = [
  {
    step: 1,
    title: "Fill Credentials Form",
    subtitle: "In the section above",
    actionBadge: "Form Inputs Required",
    badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
    icon: FileKey,
    instruction: "App Name, Username, and Password can be anything you choose. Make sure to remember your password, as you will enter this exact password inside QuickBooks Web Connector.",
    tip: "Tip: App Name, Username, and Password can be set to any values of your choice! Just remember your password.",
  },
  {
    step: 2,
    title: "Generate & Download .qwc",
    subtitle: "Click the blue button",
    actionBadge: "Click Download",
    badgeColor: "bg-[#115B9D]/10 text-[#115B9D] border-[#115B9D]/20",
    icon: Download,
    instruction: "Click 'Generate & Download .qwc File'. A small configuration file (e.g. MyCompany_Sync.qwc) will download to your computer.",
    tip: "The file contains endpoint connection routing for Web Connector.",
  },
  {
    step: 3,
    title: "Open File in Web Connector",
    subtitle: "In Downloads folder",
    actionBadge: "Double-Click File",
    badgeColor: "bg-purple-50 text-purple-700 border-purple-200",
    icon: MonitorUp,
    instruction: "Go to your Downloads folder and double-click the downloaded .qwc file. QuickBooks Web Connector will open automatically on your screen.",
    tip: "You can also launch Web Connector from File > App Management > Update Web Services inside QuickBooks.",
  },
  {
    step: 4,
    title: "Authorize QB Access Prompt",
    subtitle: "Inside QuickBooks Desktop",
    actionBadge: "Select 'Always Allow'",
    badgeColor: "bg-amber-50 text-amber-700 border-amber-200",
    icon: ShieldCheck,
    instruction: "QuickBooks Desktop will show a security prompt asking permission to access your company file. Select 'Yes, always allow access even if QuickBooks is not running'.",
    tip: "This allows Web Connector to run background syncs without manual prompts.",
  },
  {
    step: 5,
    title: "Enter Password in Web Connector",
    subtitle: "Inside Web Connector Window",
    actionBadge: "Input Password",
    badgeColor: "bg-teal-50 text-teal-700 border-teal-200",
    icon: KeyRound,
    instruction: "In Web Connector, check the checkbox next to your App Name, enter the exact Password you created in Step 1, and press Enter or click on the 'Save Password' button.",
    tip: "When prompted to save password, click 'Yes'.",
  },
  {
    step: 6,
    title: "Queue & Run Sync Jobs",
    subtitle: "Finalizing Synchronization",
    actionBadge: "Click Update Selected",
    badgeColor: "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6]/30",
    icon: PlayCircle,
    instruction: "Back on this page, click any of the 'Queue Sync Jobs' buttons (Customers, Products, Invoices, Estimates). In Web Connector, click 'Update Selected' to process the queue!",
    tip: "Tick the 'Auto-Run' box in Web Connector to sync automatically every 15 minutes.",
  },
];

const DESKTOP_FAQS = [
  {
    q: "Where do I find QuickBooks Web Connector on my computer?",
    a: "QuickBooks Web Connector comes pre-installed with QuickBooks Desktop. Search 'Web Connector' in your Windows Start Menu, or open QuickBooks Desktop and go to File > App Management > Update Web Services.",
  },
  {
    q: "What credentials can I use when generating the .qwc file?",
    a: "App Name, Username, and Password can be anything you prefer. However, you must remember the password you chose so you can enter it when prompted in QuickBooks Web Connector (passwords are case-sensitive).",
  },
  {
    q: "How do I make the sync run automatically in the background?",
    a: "In QuickBooks Web Connector, check the 'Auto-Run' box next to your application name and set the interval (e.g. 15 Minutes). As long as your PC is powered on, Web Connector will automatically process queued jobs.",
  },
  {
    q: "What does 'Queue Sync Jobs' do?",
    a: "Clicking a Queue button (such as Sync Invoices or Sync Customers) registers a sync task in our cloud backend. When Web Connector runs its update, it fetches all queued tasks and updates your data.",
  },
];

const ONLINE_GUIDE = [
  {
    step: "01",
    title: "Click Connect QuickBooks Online",
    description: "You'll be securely redirected to Intuit's official OAuth authorization page.",
  },
  {
    step: "02",
    title: "Sign in & Choose Company",
    description: "Log into your Intuit account and choose the company file you wish to connect.",
  },
  {
    step: "03",
    title: "Authorize & Sync",
    description: "Grant authorization permissions. Once redirected back, your integration is live!",
  },
];

const INTEGRATION_TABS = [
  {
    id: "online",
    label: "QuickBooks Online",
    badge: "Cloud OAuth 2.0",
    description: "Connect cloud-based QuickBooks Online accounts",
    icon: Globe,
  },
  {
    id: "desktop",
    label: "QuickBooks Desktop",
    badge: "Web Connector (QBWC)",
    description: "Connect on-premise QuickBooks Desktop software",
    icon: MonitorUp,
  },
];

const buildApiUrl = (path) => {
  const baseUrl = environment.api.endsWith("/")
    ? environment.api
    : `${environment.api}/`;
  return `${baseUrl}${path}`;
};

const getDownloadFileName = (contentDisposition, fallbackName) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
    contentDisposition || ""
  );
  if (!match?.[1]) return fallbackName;
  return decodeURIComponent(match[1]);
};

const getSafeQwcName = (appName) => {
  const safeName = appName.trim().replace(/[^a-z0-9-_]+/gi, "-");
  return `${safeName || "quickbooks-desktop"}.qwc`;
};

export default function QuickBookIntegration() {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "desktop" || tabParam === "online") return tabParam;
    const saved = localStorage.getItem("qb_active_tab");
    if (saved === "desktop" || saved === "online") return saved;
    return "desktop";
  });

  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [desktopForm, setDesktopForm] = useState({
    appName: "",
    username: "",
    password: "",
  });
  const [desktopDownloading, setDesktopDownloading] = useState(false);
  const [desktopSyncing, setDesktopSyncing] = useState("");
  const [desktopStatus, setDesktopStatus] = useState("");
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [queueModal, setQueueModal] = useState({ open: false, jobLabel: "" });
  const [downloadModal, setDownloadModal] = useState({ open: false, appName: "", password: "" });
  const [desktopConfig, setDesktopConfig] = useState(null);
  const [isDesktopConnected, setIsDesktopConnected] = useState(false);
  const [showReconfigureForm, setShowReconfigureForm] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;
  const location = useLocation();

  const fetchDesktopConfig = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await ApiClient.get(`qb-desktop/fetch-qwc?companyId=${companyId}`);
      if (res?.success && res?.data) {
        setDesktopConfig(res.data);
        setIsDesktopConnected(true);
      } else {
        setDesktopConfig(null);
        setIsDesktopConnected(false);
      }
    } catch (err) {
      console.error("Error fetching Desktop QWC config:", err);
    }
  }, [companyId]);

  useEffect(() => {
    if (activeTab === "desktop" && companyId) {
      fetchDesktopConfig();
    }
  }, [activeTab, companyId, fetchDesktopConfig]);

  useEffect(() => {
    if (user?.quick_book_accesToken) {
      setConnected(true);
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const realmId = params.get("realmId");
    const tabParam = params.get("tab") || location.state?.tab;

    if (code && realmId) {
      handleCallback(params);
      setActiveTab("online");
      localStorage.setItem("qb_active_tab", "online");
    } else if (tabParam === "desktop" || tabParam === "online") {
      setActiveTab(tabParam);
      localStorage.setItem("qb_active_tab", tabParam);
    }
  }, [location.search, location.state]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem("qb_active_tab", tabId);
  };

  const handleCallback = async (params) => {
    const userId = user?.id || user?._id;
    loader(true);
    setConnecting(true);

    const queryString = params.toString();
    const fullQuery = userId ? `${queryString}&userId=${userId}` : queryString;

    await ApiClient.get(`quickbook/callback?${fullQuery}`)
      .then((res) => {
        if (res?.success) {
          setConnected(true);
          dispatch(
            login_success({
              ...user,
              quick_book_accesToken: res?.data?.access_token || true,
              quick_book_companyId: methodModel.getPrams("realmId"),
            })
          );
          toast.success("Successfully connected to QuickBooks Online!");
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("QuickBooks Online connection failed. Please try again.");
      });

    loader(false);
    setConnecting(false);
  };

  const handleConnect = async () => {
    setConnecting(true);
    loader(true);
    await ApiClient.get("quickbook/requestToken")
      .then((res) => {
        if (res?.success && res?.payload?.url) {
          window.location.href = res?.payload?.url;
        } else {
          toast.error("Unable to generate QuickBooks connection link.");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to request token from QuickBooks.");
      });
    loader(false);
    setConnecting(false);
  };

  const handleDisconnect = () => {
    swal({
      title: "Disconnect QuickBooks Online?",
      text: "Are you sure you want to disconnect your QuickBooks Online integration?",
      icon: "warning",
      buttons: ["Cancel", "Yes, Disconnect"],
      dangerMode: true,
    }).then((willDisconnect) => {
      if (willDisconnect) {
        loader(true);
        ApiClient.post("quickbook/disconnect", { userId: user?.id || user?._id }).then(
          (res) => {
            if (res.success) {
              setConnected(false);
              dispatch(login_success({ ...user, quick_book_accesToken: null }));
              toast.success("QuickBooks Online disconnected.");
            } else {
              toast.error(res?.message || "Failed to disconnect QuickBooks.");
            }
            loader(false);
          }
        );
      }
    });
  };

  const handleDesktopDisconnect = () => {
    if (!companyId) {
      toast.error("Company ID is missing.");
      return;
    }
    swal({
      title: "Disconnect QuickBooks Desktop?",
      text: "Are you sure you want to remove this QuickBooks Desktop connection? You will need to generate a new .qwc file to re-pair.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Disconnect"],
      dangerMode: true,
    }).then(async (willDisconnect) => {
      if (willDisconnect) {
        loader(true);
        try {
          let res = await ApiClient.delete(`qb-desktop/delete-qwc?companyId=${companyId}`);
          if (!res?.success) {
            res = await ApiClient.get(`qb-desktop/delete-qwc?companyId=${companyId}`);
          }
          if (res?.success) {
            setIsDesktopConnected(false);
            setDesktopConfig(null);
            setShowReconfigureForm(false);
            setDesktopStatus("");
          } else {
            toast.error(res?.message || "Failed to disconnect QuickBooks Desktop.");
          }
        } catch (err) {
          console.error("Disconnect error:", err);
          try {
            const fallbackRes = await ApiClient.get(`qb-desktop/delete-qwc?companyId=${companyId}`);
            if (fallbackRes?.success) {
              setIsDesktopConnected(false);
              setDesktopConfig(null);
              setShowReconfigureForm(false);
              setDesktopStatus("");
              return;
            }
          } catch (fErr) {
            console.error("Fallback error:", fErr);
          }
          toast.error("Failed to disconnect QuickBooks Desktop.");
        } finally {
          loader(false);
        }
      }
    });
  };

  const handleDesktopFieldChange = (e) => {
    const { name, value } = e.target;
    setDesktopForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDesktopConnect = async (e) => {
    e.preventDefault();

    const appName = desktopForm.appName.trim();
    const username = desktopForm.username.trim();
    const password = desktopForm.password;

    if (!appName || !username || !password) {
      toast.error("Please enter App Name, Username, and Password.");
      return;
    }

    const params = new URLSearchParams({ appName, username, password });
    const url = buildApiUrl(`qb-desktop/generate-qwc?${params.toString()}`);

    setDesktopDownloading(true);
    loader(true);

    try {
      setAuthorizationToken(axios);
      const response = await axios.get(url, { responseType: "blob" });
      const contentType = response.headers["content-type"] || "";

      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const data = JSON.parse(text);
        const downloadUrl = data?.downloadUrl || data?.url || data?.fileUrl;

        if (downloadUrl) {
          window.open(downloadUrl, "_blank");
          setDesktopStatus("Connection file generated. Open it with Web Connector.");
          setDownloadModal({ open: true, appName, password });
          setDesktopForm({ appName: "", username: "", password: "" });
          fetchDesktopConfig();
          return;
        }

        throw new Error(
          data?.message ||
          data?.error?.message ||
          "QuickBooks Desktop connection file was not returned."
        );
      }

      const blob = new Blob([response.data], {
        type: contentType || "application/vnd.intuit.qwc",
      });
      const fileName = getDownloadFileName(
        response.headers["content-disposition"],
        getSafeQwcName(appName)
      );
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = downloadUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);

      setDesktopStatus("Connection file downloaded. Open it with Web Connector.");
      setDownloadModal({ open: true, appName, password });
      setDesktopForm({ appName: "", username: "", password: "" });
      fetchDesktopConfig();
    } catch (err) {
      toast.error(
        err?.message || "Unable to generate the QuickBooks Desktop connection file."
      );
    } finally {
      loader(false);
      setDesktopDownloading(false);
    }
  };

  const handleDesktopSync = async (job) => {
    if (!companyId) {
      toast.error("Company ID is missing. Please sign in again.");
      return;
    }

    setDesktopSyncing(job.key);
    loader(true);
    try {
      const res = await ApiClient.post(job.endpoint, { companyId });

      if (res?.success) {
        // Open Queue Next Steps Popup Modal instead of showing a plain toast
        setQueueModal({ open: true, jobLabel: job.label });
      } else {
        toast.error(res?.message || `Unable to queue ${job.label.toLowerCase()} sync.`);
      }
    } catch (err) {
      toast.error(`Unable to queue ${job.label.toLowerCase()} sync.`);
    } finally {
      loader(false);
      setDesktopSyncing("");
    }
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto pb-10">
        {/* Header Banner */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">QuickBooks Integration Settings</h1>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#115B9D]/10 text-[#115B9D]">
                <Zap className="size-3.5" /> Direct Sync
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Connect your accounting platform to synchronize Customers, Products, Invoices, and Estimates.
            </p>
          </div>
        </div>

        {/* Outer Card */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Top Selector Tabs */}
          <div className="border-b border-gray-200 bg-gray-50/70 p-2 sm:p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INTEGRATION_TABS.map((tab) => {
                const TabIcon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-start gap-3.5 text-left rounded-xl p-3.5 border transition-all ${isActive
                      ? "bg-white border-[#115B9D]/40 text-gray-900 shadow-sm ring-2 ring-[#115B9D]/10"
                      : "bg-white/60 border-gray-200/80 text-gray-500 hover:text-gray-900 hover:bg-white"
                      }`}
                  >
                    <span
                      className={`size-11 rounded-xl flex items-center justify-center shrink-0 transition-all ${isActive
                        ? "bg-[#115B9D] text-white shadow-md shadow-[#115B9D]/25"
                        : "bg-gray-100 border border-gray-200 text-gray-500"
                        }`}
                    >
                      <TabIcon className="size-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="block text-sm font-bold text-gray-900">
                          {tab.label}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isActive
                            ? "bg-[#115B9D]/10 text-[#115B9D] border-[#115B9D]/20"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                            }`}
                        >
                          {tab.badge}
                        </span>
                      </span>
                      <span className="block text-xs text-gray-500 mt-1 truncate">
                        {tab.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-5 sm:p-7">
            {/* ── QUICKBOOKS ONLINE TAB ── */}
            {activeTab === "online" ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
                  {/* Left Column: Status & Connect */}
                  <section className="border border-gray-200 rounded-2xl bg-white p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">
                          QuickBooks Online Integration
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Authorize cloud connection via Intuit OAuth 2.0.
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border ${connected
                          ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6]/30"
                          : "bg-gray-100 text-gray-600 border-gray-200"
                          }`}
                      >
                        <span
                          className={`size-2 rounded-full ${connected ? "bg-[#008236] animate-pulse" : "bg-gray-400"
                            }`}
                        />
                        {connected ? "QuickBooks Online Connected" : "Not Connected"}
                      </span>
                    </div>

                    {/* Feature Card */}
                    <div className="rounded-2xl border border-[#115B9D]/15 bg-gradient-to-br from-[#115B9D]/5 via-white to-gray-50/50 p-6 mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="size-12 rounded-2xl bg-[#2CA01C]/10 border border-[#2CA01C]/20 flex items-center justify-center text-[#2CA01C] shrink-0">
                          <Globe className="size-6" />
                        </div>
                        <div>
                          <p className="text-base font-bold text-gray-900">
                            Cloud Accounting Synchronization
                          </p>
                          <p className="text-xs text-gray-500">
                            Connect your cloud QuickBooks account for automated data exchange.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                        {[
                          { icon: RefreshCw, title: "Automatic Sync", text: "No manual files required" },
                          { icon: ShieldCheck, title: "OAuth 2.0 Security", text: "Intuit certified encryption" },
                          { icon: Sparkles, title: "Instant Access", text: "One-click connection" },
                        ].map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <div
                              key={item.title}
                              className="flex flex-col gap-1 rounded-xl bg-white border border-gray-200 p-3 shadow-2xs"
                            >
                              <ItemIcon className="size-4 text-[#115B9D]" />
                              <span className="text-xs font-semibold text-gray-800 mt-1">
                                {item.title}
                              </span>
                              <span className="text-[11px] text-gray-500">
                                {item.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="pt-2">
                      {connected ? (
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={handleDisconnect}
                            className="inline-flex items-center justify-center gap-2 border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold px-5 py-2.5 rounded-xl transition-all"
                          >
                            <Link2 className="size-4" />
                            Disconnect QuickBooks Online
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleConnect}
                          disabled={connecting}
                          className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all shadow-md shadow-[#115B9D]/25"
                        >
                          {connecting ? (
                            <>
                              <RefreshCw className="size-4 animate-spin" />
                              Connecting to Intuit...
                            </>
                          ) : (
                            <>
                              <Link2 className="size-4" />
                              Connect QuickBooks Online
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </section>

                  {/* Right Column: Security Info */}
                  <aside className="border border-gray-200 rounded-2xl bg-[#F8FAFC] p-6 flex flex-col justify-between">
                    <div>
                      <div className="size-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-[#115B9D] mb-4 shadow-2xs">
                        <Lock className="size-5" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900">
                        OAuth 2.0 Security
                      </h3>
                      <p className="text-xs text-gray-600 leading-5 mt-2">
                        Your credentials are never stored on our servers. Intuit handles login and issues an encrypted access token.
                      </p>

                      <div className="mt-5 space-y-3">
                        <div className="p-3 rounded-xl bg-white border border-gray-200">
                          <p className="text-[11px] uppercase font-bold text-[#115B9D] tracking-wide">
                            Revocable Permissions
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            You can disconnect or revoke access at any time from this dashboard or inside your Intuit Account settings.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 flex items-center gap-2">
                      <ShieldCheck className="size-4 text-[#008236] shrink-0" />
                      256-bit SSL Encrypted Connection
                    </div>
                  </aside>
                </div>

                {/* Step-by-Step Online Guide */}
                <section className="border border-gray-200 rounded-2xl bg-white p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <HelpCircle className="size-5 text-[#115B9D]" />
                    <h3 className="text-base font-bold text-gray-900">
                      QuickBooks Online Connection Guide
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {ONLINE_GUIDE.map((step) => (
                      <div
                        key={step.step}
                        className="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex flex-col"
                      >
                        <span className="size-8 rounded-lg bg-[#115B9D] text-white flex items-center justify-center text-xs font-bold mb-3 shadow-xs">
                          {step.step}
                        </span>
                        <p className="text-sm font-bold text-gray-900">
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 leading-5">
                          {step.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            ) : (
              <div className="space-y-6">
                {/* ── QUICKBOOKS DESKTOP TAB ── */}
                {/* Top Section: Form + System Requirements */}
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
                  {/* Left Column: QWC Generator */}
                  <section className="border border-gray-200 rounded-2xl bg-white p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">
                          QuickBooks Desktop Web Connector Setup
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Generate a `.qwc` configuration file for QuickBooks Web Connector (QBWC).
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border ${isDesktopConnected
                          ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6]/30"
                          : "bg-[#115B9D]/10 text-[#115B9D] border-[#115B9D]/20"
                          }`}
                      >
                        <span
                          className={`size-2 rounded-full ${isDesktopConnected ? "bg-[#008236] animate-pulse" : "bg-[#115B9D]"
                            }`}
                        />
                        {isDesktopConnected ? "Desktop Connection Active" : "QBWC Compatible"}
                      </span>
                    </div>

                    {/* Active Desktop Connection Panel if already connected */}
                    {isDesktopConnected && desktopConfig ? (
                      <div className="space-y-4 mb-5">
                        <div className="rounded-2xl border border-emerald-200 bg-[#F0FEF4] p-5 shadow-xs">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex items-start gap-3.5">
                              <div className="size-12 rounded-2xl bg-[#008236] text-white flex items-center justify-center font-bold shadow-xs shrink-0">
                                <CheckCircle2 className="size-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-base font-bold text-gray-900">
                                    {desktopConfig.appName || "QuickBooks Desktop Connection"}
                                  </h3>
                                  <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-[#008236]/15 text-[#008236] border border-[#008236]/25">
                                    Active & Connected
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  Your company is paired with QuickBooks Web Connector (QBWC). Multiple connections are disabled.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-3 border-t border-emerald-200/60 text-xs">
                                  <div>
                                    <span className="text-gray-500 font-medium block">Application Name</span>
                                    <span className="font-bold text-gray-900">{desktopConfig.appName || "—"}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 font-medium block">Web Connector Username</span>
                                    <span className="font-bold text-gray-900">{desktopConfig.username || "—"}</span>
                                  </div>
                                  {desktopConfig.createdAt && (
                                    <div>
                                      <span className="text-gray-500 font-medium block">Paired On</span>
                                      <span className="font-bold text-gray-900">
                                        {new Date(desktopConfig.createdAt).toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-gray-500 font-medium block">Sync Status</span>
                                    <span className="font-bold text-[#008236]">Ready for Web Connector</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setShowReconfigureForm((prev) => !prev)}
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white border border-gray-200 px-3.5 py-2 rounded-xl transition-all shadow-xs"
                              >
                                {showReconfigureForm ? "Cancel" : "Re-generate .qwc File"}
                              </button>

                              <button
                                type="button"
                                onClick={handleDesktopDisconnect}
                                className="text-xs font-semibold text-red-600 hover:text-red-700 bg-white border border-red-200 hover:bg-red-50 px-3.5 py-2 rounded-xl transition-all shadow-xs flex items-center gap-1.5"
                              >
                                Disconnect
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Optional Re-generate Form if user explicitly requests it */}
                        {showReconfigureForm && (
                          <div className="border border-amber-200 rounded-2xl bg-amber-50/50 p-5 space-y-4">
                            <div className="flex items-start gap-2.5">
                              <AlertCircle className="size-4 text-amber-600 mt-0.5 shrink-0" />
                              <p className="text-xs text-amber-800 leading-5">
                                <strong>Warning:</strong> Generating a new connection file will update your Web Connector credentials. You will need to replace the <code>.qwc</code> file in QuickBooks Web Connector.
                              </p>
                            </div>

                            <form onSubmit={handleDesktopConnect} className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-1.5">
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    App Name <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name="appName"
                                    value={desktopForm.appName}
                                    onChange={handleDesktopFieldChange}
                                    placeholder="e.g. ClientPortal_Sync"
                                    className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                                  />
                                  <span className="text-[11px] text-gray-500">Can be any name of your choice</span>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    Username <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name="username"
                                    value={desktopForm.username}
                                    onChange={handleDesktopFieldChange}
                                    placeholder="e.g. sync_admin"
                                    className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                                  />
                                  <span className="text-[11px] text-gray-500">Can be any username of your choice</span>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    New Password <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="password"
                                    name="password"
                                    value={desktopForm.password}
                                    onChange={handleDesktopFieldChange}
                                    placeholder="Enter new password"
                                    className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                                  />
                                  <span className="text-[11px] text-amber-700 font-medium">Can be any password — remember it!</span>
                                </div>
                              </div>

                              <div className="flex justify-end gap-3 pt-2">
                                <button
                                  type="submit"
                                  disabled={desktopDownloading}
                                  className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-90 disabled:opacity-60 text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-all shadow-md shadow-[#115B9D]/25"
                                >
                                  {desktopDownloading ? (
                                    <>
                                      <RefreshCw className="size-4 animate-spin" />
                                      Generating New File...
                                    </>
                                  ) : (
                                    <>
                                      <Download className="size-4" />
                                      Generate & Download New .qwc File
                                    </>
                                  )}
                                </button>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 mb-5 flex items-start gap-3">
                          <AlertCircle className="size-5 text-amber-600 mt-0.5 shrink-0" />
                          <p className="text-xs text-amber-800 leading-5">
                            <strong className="font-semibold">Prerequisite:</strong> Ensure QuickBooks Desktop and QuickBooks Web Connector (QBWC) are installed and running on the host PC.
                          </p>
                        </div>

                        <form onSubmit={handleDesktopConnect} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                App Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="appName"
                                value={desktopForm.appName}
                                onChange={handleDesktopFieldChange}
                                placeholder="e.g. ClientPortal_Sync"
                                className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                              />
                              <span className="text-[11px] text-gray-500">Can be any name of your choice</span>
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                Username <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="username"
                                value={desktopForm.username}
                                onChange={handleDesktopFieldChange}
                                placeholder="e.g. sync_admin"
                                className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                              />
                              <span className="text-[11px] text-gray-500">Can be any username of your choice</span>
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                name="password"
                                value={desktopForm.password}
                                onChange={handleDesktopFieldChange}
                                placeholder="Enter password"
                                className="h-11 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                              />
                              <span className="text-[11px] text-amber-700 font-medium">Can be any password — remember it!</span>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5 text-xs text-amber-900 bg-amber-50/90 p-3 rounded-xl border border-amber-200/80">
                            <Key className="size-4 text-amber-600 mt-0.5 shrink-0" />
                            <span>
                              <strong>Important:</strong> App Name, Username, and Password can be set to anything you prefer. <strong>Remember your password</strong> as you will enter this exact password inside QuickBooks Web Connector when authorizing the file.
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 pt-2">
                            <button
                              type="submit"
                              disabled={desktopDownloading}
                              className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-all shadow-md shadow-[#115B9D]/25"
                            >
                              {desktopDownloading ? (
                                <>
                                  <RefreshCw className="size-4 animate-spin" />
                                  Generating File...
                                </>
                              ) : (
                                <>
                                  <Download className="size-4" />
                                  Generate & Download .qwc File
                                </>
                              )}
                            </button>

                            {desktopStatus && (
                              <p className="inline-flex items-center gap-1.5 text-xs text-[#008236] font-semibold bg-[#F0FEF4] border border-[#008236]/30 px-3 py-2 rounded-xl">
                                <CheckCircle2 className="size-4" />
                                {desktopStatus}
                              </p>
                            )}
                          </div>
                        </form>
                      </>
                    )}
                  </section>

                  {/* Right Column: Requirements Checklist */}
                  <aside className="border border-gray-200 rounded-2xl bg-[#F8FAFC] p-6">
                    <div className="size-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-[#115B9D] mb-4 shadow-2xs">
                      <CheckCircle2 className="size-5" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">
                      System Requirements
                    </h3>

                    <div className="mt-3 space-y-3">
                      {DESKTOP_REQUIREMENTS.map((req) => (
                        <div key={req.title} className="flex items-start gap-2.5">
                          <CheckCircle2 className="size-4 text-[#008236] mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-gray-800">
                              {req.title}
                            </p>
                            <p className="text-[11px] text-gray-500 leading-4 mt-0.5">
                              {req.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 p-3 rounded-xl bg-white border border-gray-200">
                      <p className="text-[11px] uppercase font-bold text-gray-500">
                        Job Execution
                      </p>
                      <p className="text-xs text-gray-700 mt-0.5">
                        Jobs are processed sequentially by Web Connector during auto-run or manual update.
                      </p>
                    </div>
                  </aside>
                </div>

                {/* Middle Section: Queue Sync Jobs */}
                <section className="border border-gray-200 rounded-2xl bg-white p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">
                        Queue Desktop Sync Jobs
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Select records to queue for Web Connector sync.
                      </p>
                    </div>
                    {companyId && (
                      <span className="text-xs font-semibold text-[#115B9D] bg-[#115B9D]/10 border border-[#115B9D]/20 rounded-xl px-3 py-1.5">
                        Company ID: {companyId}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {DESKTOP_SYNC_JOBS.map((job) => {
                      const ButtonIcon = job.icon;
                      const isLoading = desktopSyncing === job.key;

                      return (
                        <button
                          key={job.key}
                          type="button"
                          onClick={() => handleDesktopSync(job)}
                          disabled={!companyId || !!desktopSyncing}
                          className="group min-h-[120px] rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#115B9D] hover:shadow-md hover:shadow-[#115B9D]/10 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none transition-all flex flex-col justify-between"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className={`size-10 rounded-xl bg-gradient-to-br ${job.color} text-white flex items-center justify-center shadow-xs`}>
                              {isLoading ? (
                                <RefreshCw className="size-4 animate-spin" />
                              ) : (
                                <ButtonIcon className="size-4" />
                              )}
                            </span>
                            <span className="text-xs font-bold text-[#115B9D] bg-[#115B9D]/10 px-2.5 py-1 rounded-lg group-hover:bg-[#115B9D] group-hover:text-white transition-colors">
                              + Queue
                            </span>
                          </div>
                          <div>
                            <span className="block text-sm font-bold text-gray-900 mt-2">
                              Sync {job.label}
                            </span>
                            <span className="block text-xs leading-4 text-gray-500 mt-1">
                              {job.description}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* DESKTOP SETUP ROADMAP & VISUAL GUIDE */}
                <section className="border border-gray-200 rounded-2xl bg-white p-6 sm:p-7 shadow-xs">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#115B9D]/10 text-[#115B9D] flex items-center justify-center font-bold">
                        <MonitorUp className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Step-by-Step Desktop Setup Roadmap
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Follow this 6-step walkthrough to pair QuickBooks Desktop with Web Connector.
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
                      <Sparkles className="size-3.5" /> Interactive Guide
                    </span>
                  </div>

                  {/* 6 Step Interactive Roadmap Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                    {ENHANCED_DESKTOP_STEPS.map((item) => {
                      const StepIcon = item.icon;
                      return (
                        <div
                          key={item.step}
                          className="relative rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50/70 to-white p-5 flex flex-col justify-between hover:shadow-md hover:border-[#115B9D]/40 transition-all group"
                        >
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-3">
                              <span className="size-8 rounded-xl bg-[#115B9D] text-white font-bold text-xs flex items-center justify-center shadow-xs">
                                0{item.step}
                              </span>
                              <span
                                className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${item.badgeColor}`}
                              >
                                {item.actionBadge}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 mb-1.5">
                              <StepIcon className="size-4 text-[#115B9D] shrink-0" />
                              <h4 className="text-sm font-bold text-gray-900 group-hover:text-[#115B9D] transition-colors">
                                {item.title}
                              </h4>
                            </div>

                            <p className="text-xs text-gray-600 leading-5">
                              {item.instruction}
                            </p>
                          </div>

                          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-1.5 text-[11px] text-gray-500 italic">
                            <Info className="size-3.5 text-[#115B9D] shrink-0" />
                            <span>{item.tip}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop FAQs & Troubleshooting Accordion */}
                  <div className="border border-gray-200 rounded-xl bg-gray-50/50 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <HelpCircle className="size-5 text-[#115B9D]" />
                      <h4 className="text-sm font-bold text-gray-900">
                        Frequently Asked Questions & Troubleshooting
                      </h4>
                    </div>

                    <div className="space-y-3">
                      {DESKTOP_FAQS.map((faq, index) => {
                        const isOpen = openFaqIndex === index;
                        return (
                          <div
                            key={faq.q}
                            className="border border-gray-200 bg-white rounded-xl overflow-hidden transition-all"
                          >
                            <button
                              type="button"
                              onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                              className="w-full px-4 py-3 text-left flex items-center justify-between gap-3 text-xs font-bold text-gray-900 hover:bg-gray-50 transition-colors"
                            >
                              <span className="flex items-center gap-2">
                                <span className="size-5 rounded-md bg-[#115B9D]/10 text-[#115B9D] flex items-center justify-center text-[10px] font-bold">
                                  Q
                                </span>
                                {faq.q}
                              </span>
                              {isOpen ? (
                                <ChevronUp className="size-4 text-gray-500 shrink-0" />
                              ) : (
                                <ChevronDown className="size-4 text-gray-500 shrink-0" />
                              )}
                            </button>
                            {isOpen && (
                              <div className="px-4 pb-3.5 pt-1 text-xs text-gray-600 leading-5 border-t border-gray-100 bg-gray-50/40">
                                {faq.a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Queue Next Steps Guidance Popup Modal */}
      <Transition appear show={queueModal.open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setQueueModal({ open: false, jobLabel: "" })}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 border border-gray-200 text-left">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#F0FEF4] border border-[#0a7822a6]/20 text-[#008236] flex items-center justify-center shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          {queueModal.jobLabel} Sync Queue Created
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Ready for QuickBooks Web Connector
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setQueueModal({ open: false, jobLabel: "" })}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <p className="text-xs font-semibold text-[#115B9D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <PlayCircle className="size-4" /> Next Steps to Complete Sync
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            1
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Open <strong>QuickBooks Web Connector (QBWC)</strong> on your computer.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            2
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Ensure the checkbox <strong className="text-[#115B9D]">[ ✓ ]</strong> next to your Application Name is checked.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            3
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Click the <strong className="text-[#008236]">"Update Selected"</strong> button at the top of Web Connector to process the queue now!
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-5">
                      <Sparkles className="size-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>
                        <strong>Pro Tip:</strong> Check the <strong>Auto-Run</strong> box in Web Connector to automatically process queue jobs every 15 minutes.
                      </span>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setQueueModal({ open: false, jobLabel: "" })}
                      className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium px-5 py-2.5 rounded-xl transition-all"
                    >
                      Got it, Go to Web Connector
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* File Download Guidance Popup Modal */}
      <Transition appear show={downloadModal.open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDownloadModal({ open: false, appName: "", password: "" })}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 border border-gray-200 text-left">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#F0FEF4] border border-[#0a7822a6]/20 text-[#008236] flex items-center justify-center shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          .qwc Connection File Downloaded!
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Follow these 3 quick steps to finish pairing
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDownloadModal({ open: false, appName: "", password: "" })}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <p className="text-xs font-semibold text-[#115B9D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <PlayCircle className="size-4" /> Next Steps in Web Connector
                      </p>

                      <div className="space-y-3">
                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            1
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Open the downloaded <strong>{downloadModal.appName || "QuickBooks"}.qwc</strong> file on your computer to add it to <strong>QuickBooks Web Connector (QBWC)</strong>.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            2
                          </span>
                          <div className="text-xs text-gray-700 leading-5">
                            In Web Connector, enter your password:{" "}
                            <code className="px-2 py-0.5 bg-white border border-gray-300 rounded font-mono font-bold text-[#115B9D] text-xs select-all">
                              {downloadModal.password || "••••••••"}
                            </code>{" "}
                            and press Enter or click on <strong>"Save Password"</strong> button (select "Yes" to save).
                          </div>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            3
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Return here and click any of the <strong>Queue Sync Jobs</strong> buttons below to start syncing!
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-5">
                      <Sparkles className="size-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>
                        <strong>Pro Tip:</strong> Check the <strong>Auto-Run</strong> box next to your App Name in Web Connector to automatically run syncs every 15 minutes.
                      </span>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setDownloadModal({ open: false, appName: "", password: "" })}
                      className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium px-5 py-2.5 rounded-xl transition-all"
                    >
                      Got it, Ready to Queue!
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}